import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingCart, Check, Star, Send } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ count: 0, average_rating: 0 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ author_name: '', rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data.reviews);
      setReviewStats({ count: response.data.count, average_rating: response.data.average_rating });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);

        const relatedResponse = await axios.get(`${API}/products?category=${response.data.category}`);
        setRelatedProducts(relatedResponse.data.filter(p => p.id !== id).slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    fetchReviews();
  }, [id, navigate]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.author_name.trim() || !reviewForm.comment.trim()) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: id,
        ...reviewForm
      });
      toast.success('Dziękujemy za opinię!');
      setShowReviewForm(false);
      setReviewForm({ author_name: '', rating: 5, comment: '' });
      fetchReviews();
    } catch (error) {
      toast.error('Nie udało się dodać opinii');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${product.name} ${t.product.addToCart.toLowerCase()}`);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const getCategoryName = (category) => {
    switch(category) {
      case 'dewalt-batteries': return t.nav.powerTools;
      case 'milwaukee-batteries': return t.nav.handTools;
      case 'makita-batteries': return t.nav.accessories;
      case 'tools-accessories': return t.nav.toolsAccessories;
      default: return category;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-6 bg-zinc-800 w-32 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="aspect-square bg-zinc-800" />
              <div className="space-y-4">
                <div className="h-4 bg-zinc-800 w-24" />
                <div className="h-10 bg-zinc-800 w-3/4" />
                <div className="h-8 bg-zinc-800 w-32" />
                <div className="h-32 bg-zinc-800" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/shop" 
          className="inline-flex items-center gap-2 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={16} /> {t.product.backToShop}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          <div className="border border-zinc-800 bg-[#111111] overflow-hidden group cursor-zoom-in">
            <div className="aspect-square overflow-hidden">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-500 ease-out" data-testid="product-image" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-600">
                  <span className="font-mono text-sm">No Image</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2" data-testid="product-category">
              {getCategoryName(product.category)}
            </span>

            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-3xl text-yellow-500" data-testid="product-price">
                {formatPrice(product.price)}
              </span>
              {product.is_bestseller && (
                <span className="bg-yellow-400 text-black font-mono text-xs tracking-widest uppercase px-3 py-1">
                  {t.product.bestseller}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mb-6">
              {product.stock > 0 ? (
                <>
                  <Check size={16} className="text-green-500" />
                  <span className="text-green-500 font-mono text-sm">{t.product.inStock} ({product.stock} {t.product.available})</span>
                </>
              ) : (
                <span className="text-red-500 font-mono text-sm">{t.product.outOfStock}</span>
              )}
            </div>

            <p className="text-zinc-300 text-lg leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-auto">
              <div className="flex items-center border border-zinc-700 bg-[#111111]">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-4 text-zinc-400 hover:text-white transition-colors" data-testid="quantity-decrease">
                  <Minus size={20} />
                </button>
                <span className="px-6 font-mono text-lg text-white" data-testid="quantity-value">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="p-4 text-zinc-400 hover:text-white transition-colors" data-testid="quantity-increase">
                  <Plus size={20} />
                </button>
              </div>

              <button 
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart size={20} /> {t.product.addToCart}
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-zinc-800">
              <h3 className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-4">{t.product.features}</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-zinc-300"><Check size={16} className="text-yellow-400" /> {t.product.professionalGrade}</li>
                <li className="flex items-center gap-2 text-zinc-300"><Check size={16} className="text-yellow-400" /> {t.product.manufacturerWarranty}</li>
                <li className="flex items-center gap-2 text-zinc-300"><Check size={16} className="text-yellow-400" /> {t.product.fastDelivery}</li>
              </ul>
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="mt-16 pt-16 border-t border-zinc-800">
            <h2 className="font-heading text-3xl uppercase text-white mb-8">{t.product.relatedProducts}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
              {relatedProducts.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="border border-zinc-800 bg-[#050505] group flex flex-col hover:border-yellow-400 transition-colors">
                  <div className="aspect-square bg-[#111111] overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg uppercase text-white group-hover:text-yellow-400 transition-colors">{p.name}</h3>
                    <span className="font-mono text-lg text-yellow-500">{formatPrice(p.price)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="mt-16 pt-16 border-t border-zinc-800" data-testid="reviews-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-3xl uppercase text-white">Opinie klientów</h2>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      size={20} 
                      className={star <= Math.round(reviewStats.average_rating) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'} 
                    />
                  ))}
                </div>
                <span className="text-zinc-400">
                  {reviewStats.average_rating.toFixed(1)} ({reviewStats.count} {reviewStats.count === 1 ? 'opinia' : 'opinii'})
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 transition-colors"
            >
              Dodaj opinię
            </button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form onSubmit={handleSubmitReview} className="bg-[#111111] border border-zinc-800 p-6 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">Twoje imię</label>
                  <input
                    type="text"
                    value={reviewForm.author_name}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, author_name: e.target.value }))}
                    className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-3 focus:border-yellow-400 focus:outline-none"
                    placeholder="Jan"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">Ocena</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                        className="p-1"
                      >
                        <Star 
                          size={28} 
                          className={star <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600 hover:text-yellow-400'} 
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">Twoja opinia</label>
                <textarea
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                  rows={4}
                  className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-3 focus:border-yellow-400 focus:outline-none resize-none"
                  placeholder="Podziel się swoją opinią o produkcie..."
                />
              </div>
              <button
                type="submit"
                disabled={submittingReview}
                className="bg-yellow-400 hover:bg-yellow-300 disabled:bg-zinc-700 text-black font-bold px-6 py-3 flex items-center gap-2 transition-colors"
              >
                <Send size={18} />
                {submittingReview ? 'Wysyłanie...' : 'Wyślij opinię'}
              </button>
            </form>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-[#111111] border border-zinc-800 p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white font-bold">{review.author_name}</p>
                      <div className="flex mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            size={14} 
                            className={star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'} 
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-zinc-500 text-sm">
                      {new Date(review.created_at).toLocaleDateString('pl-PL')}
                    </span>
                  </div>
                  <p className="text-zinc-300">{review.comment}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#111111] border border-zinc-800 p-8 text-center">
              <p className="text-zinc-400">Brak opinii. Bądź pierwszy!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
